import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  CalendarDays, ChevronLeft, ChevronRight, Check, X, Clock,
  Sparkles, Thermometer, AlertTriangle, User, Filter,
  Briefcase, AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GIORNI_SETTIMANA = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const GIORNI_SETTIMANA_FULL = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const FILTRI_TIPO = [
  { value: "all", label: "Tutto", icon: CalendarDays },
  { value: "pulizie", label: "Pulizie", icon: Sparkles, color: "text-cyan-400" },
  { value: "gestione", label: "Gestione", icon: Briefcase, color: "text-orange-400" },
  { value: "temperature", label: "Temperature", icon: Thermometer, color: "text-blue-400" },
];

const CalendarioAdmin = () => {
  const { getAuthHeaders, localeCorrente } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("giorno"); // giorno | settimana

  // Sync viewMode with ?view= query param (used by wizard to force settimana)
  useEffect(() => {
    if (searchParams.get("view") === "settimana") {
      setViewMode("settimana");
    }
  }, [searchParams]);
  const [filtroTipo, setFiltroTipo] = useState("all");
  
  // Data
  const [dayData, setDayData] = useState({ pulizie: [], gestione: [], temperature: [], stats: {} });
  const [weekData, setWeekData] = useState([]);
  const [operatori, setOperatori] = useState([]);

  useEffect(() => {
    if (localeCorrente?.id) {
      fetchOperatori();
      if (viewMode === "giorno") {
        fetchDayData(formatDate(currentDate));
      } else {
        fetchWeekData();
      }
    }
  }, [localeCorrente?.id, currentDate, viewMode]);

  const formatDate = (date) => date.toISOString().split('T')[0];

  const fetchOperatori = async () => {
    try {
      const res = await axios.get(`${API}/utenti`, { headers: getAuthHeaders() });
      setOperatori(res.data.filter(u => u.ruolo !== "admin"));
    } catch (error) {
      console.error("Errore caricamento operatori");
    }
  };

  const getOperatoreNome = (id) => {
    const op = operatori.find(o => o.id === id);
    return op?.nome || op?.email || op?.telefono || "";
  };

  const fetchDayData = async (data) => {
    if (!localeCorrente?.id) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${API}/calendario-attivita?locale_id=${localeCorrente.id}&data=${data}`,
        { headers: getAuthHeaders() }
      );
      setDayData(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const fetchWeekData = async () => {
    if (!localeCorrente?.id) return;
    try {
      setLoading(true);
      const startOfWeek = new Date(currentDate);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
      startOfWeek.setDate(diff);
      
      const promises = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(startOfWeek);
        d.setDate(startOfWeek.getDate() + i);
        promises.push(
          axios.get(
            `${API}/calendario-attivita?locale_id=${localeCorrente.id}&data=${formatDate(d)}`,
            { headers: getAuthHeaders() }
          ).then(res => ({ date: formatDate(d), day: d, dayIndex: i, ...res.data }))
        );
      }
      const results = await Promise.all(promises);
      setWeekData(results);
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const navigateDate = (delta) => {
    const newDate = new Date(currentDate);
    if (viewMode === "giorno") {
      newDate.setDate(newDate.getDate() + delta);
    } else {
      newDate.setDate(newDate.getDate() + (delta * 7));
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  const isToday = (date) => formatDate(date) === formatDate(new Date());

  const getDateDisplay = () => {
    if (viewMode === "giorno") {
      const options = { weekday: 'long', day: 'numeric', month: 'long' };
      return currentDate.toLocaleDateString('it-IT', options);
    } else {
      const startOfWeek = new Date(currentDate);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
      startOfWeek.setDate(diff);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return `${startOfWeek.getDate()} - ${endOfWeek.getDate()} ${MESI[endOfWeek.getMonth()]}`;
    }
  };

  // Get all tasks for a day, filtered by type
  const getFilteredTasks = (data) => {
    let tasks = [];
    
    if (filtroTipo === "all" || filtroTipo === "pulizie") {
      (data.pulizie || []).forEach(t => tasks.push({ ...t, tipo_attivita: "pulizia" }));
    }
    if (filtroTipo === "all" || filtroTipo === "gestione") {
      (data.gestione || []).forEach(t => tasks.push({ ...t, tipo_attivita: "gestione" }));
    }
    if (filtroTipo === "all" || filtroTipo === "temperature") {
      (data.temperature || []).forEach(t => tasks.push({ ...t, tipo_attivita: "temperatura" }));
    }
    
    return tasks;
  };

  // Render a single task item (for list view)
  const renderTaskItem = (task, showDate = false, dateLabel = "") => {
    const isCompleted = task.completata;
    const isAnomaly = task.is_anomala || (!isCompleted && task.tipo !== "informativa");
    
    let bgColor = "bg-card";
    let borderColor = "border-border";
    let icon = null;
    let typeLabel = "";
    let typeColor = "";
    
    if (task.tipo_attivita === "pulizia") {
      icon = <Sparkles className="h-4 w-4" />;
      typeLabel = task.area_nome || "Pulizia";
      typeColor = "text-cyan-600";
      if (isCompleted) {
        bgColor = "bg-cyan-50";
        borderColor = "border-cyan-300";
      }
    } else if (task.tipo_attivita === "gestione") {
      icon = <Briefcase className="h-4 w-4" />;
      typeLabel = task.tipo === "informativa" ? "Info" : "Operativa";
      typeColor = "text-orange-600";
      if (isCompleted) {
        bgColor = "bg-orange-50";
        borderColor = "border-orange-300";
      }
    } else if (task.tipo_attivita === "temperatura") {
      icon = <Thermometer className="h-4 w-4" />;
      typeLabel = task.tipologia || "Frigorifero";
      typeColor = task.is_anomala ? "text-red-600" : "text-blue-600";
      if (isCompleted) {
        bgColor = task.is_anomala ? "bg-red-50" : "bg-blue-50";
        borderColor = task.is_anomala ? "border-red-300" : "border-blue-300";
      }
    }
    
    // Non-completed operative tasks are anomalies
    if (!isCompleted && task.tipo_attivita !== "temperatura" && task.tipo !== "informativa") {
      borderColor = "border-amber-400";
    }

    return (
      <div 
        key={`${task.tipo_attivita}-${task.id}`}
        className={`p-3 rounded-xl border ${bgColor} ${borderColor} transition-all shadow-sm`}
      >
        <div className="flex items-start gap-3">
          {/* Status Icon */}
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isCompleted 
              ? task.is_anomala ? "bg-red-100" : "bg-green-100"
              : "bg-muted"
          }`}>
            {isCompleted ? (
              task.is_anomala ? (
                <AlertTriangle className="h-4 w-4 text-red-600" />
              ) : (
                <Check className="h-4 w-4 text-green-600" />
              )
            ) : (
              <X className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            {/* Header row */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`${typeColor}`}>{icon}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full bg-muted ${typeColor} font-medium`}>
                {typeLabel}
              </span>
              {showDate && dateLabel && (
                <span className="text-xs text-muted-foreground">{dateLabel}</span>
              )}
            </div>
            
            {/* Task name */}
            <h4 className={`font-medium mt-1 ${isCompleted ? typeColor : "text-foreground"}`}>
              {task.nome}
            </h4>
            
            {/* Temperature value */}
            {task.tipo_attivita === "temperatura" && task.temperatura !== null && (
              <p className={`text-lg font-bold ${task.is_anomala ? "text-red-600" : "text-blue-600"}`}>
                {task.temperatura}°C
              </p>
            )}
            
            {/* Description if exists */}
            {task.descrizione && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{task.descrizione}</p>
            )}
            
            {/* Completion info */}
            {isCompleted && (
              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                {(task.operatore_nome || task.operatore) && (
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {task.operatore_nome || task.operatore}
                  </span>
                )}
                {(task.ora_completamento || task.ora_registrazione) && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {task.ora_completamento || task.ora_registrazione}
                  </span>
                )}
              </div>
            )}
            
            {/* Anomaly warning */}
            {task.is_anomala && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-3 w-3 text-red-600" />
                <span className="text-xs text-red-700 font-medium">
                  Fuori range ({task.temperatura_min}°C - {task.temperatura_max}°C)
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Stats summary
  const renderStats = (stats) => {
    const items = [
      { key: "pulizie", label: "Pulizie", icon: Sparkles, color: "text-cyan-600", completate: stats?.pulizie_completate || 0, totali: stats?.pulizie_totali || 0 },
      { key: "gestione", label: "Gestione", icon: Briefcase, color: "text-orange-600", completate: stats?.gestione_completate || 0, totali: stats?.gestione_totali || 0 },
      { key: "temperature", label: "Temperature", icon: Thermometer, color: "text-blue-600", completate: stats?.temperature_completate || 0, totali: stats?.temperature_totali || 0 },
    ];
    
    const filtered = filtroTipo === "all" ? items : items.filter(i => i.key === filtroTipo);
    
    return (
      <div className={`grid gap-3 ${filtered.length === 1 ? "grid-cols-1" : filtered.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
        {filtered.map(item => {
          const Icon = item.icon;
          const isComplete = item.completate === item.totali && item.totali > 0;
          return (
            <div key={item.key} className="bg-card rounded-xl p-3 border border-border shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`h-4 w-4 ${item.color}`} />
                <span className="text-muted-foreground text-xs font-medium">{item.label}</span>
              </div>
              <p className={`text-xl font-bold ${isComplete ? "text-green-600" : "text-foreground"}`}>
                {item.completate}
                <span className="text-muted-foreground">/{item.totali}</span>
              </p>
            </div>
          );
        })}
      </div>
    );
  };

  // Table view for week
  const renderWeekTable = () => {
    // Collect all unique task names across the week
    const allTasksMap = new Map();
    
    weekData.forEach(dayData => {
      const tasks = getFilteredTasks(dayData);
      tasks.forEach(task => {
        const key = `${task.tipo_attivita}-${task.nome}`;
        if (!allTasksMap.has(key)) {
          allTasksMap.set(key, {
            nome: task.nome,
            tipo_attivita: task.tipo_attivita,
            area_nome: task.area_nome,
            tipologia: task.tipologia,
            tipo: task.tipo,
            operatori_ids: task.operatori_ids || []
          });
        }
      });
    });
    
    const allTasks = Array.from(allTasksMap.values());
    
    if (allTasks.length === 0) {
      return (
        <div className="text-center py-12 text-muted-foreground bg-card rounded-2xl border border-border">
          <CalendarDays className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
          <p>Nessuna attività per questa settimana</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse bg-card rounded-xl shadow-sm border border-border">
          <thead>
            <tr>
              <th className="text-left p-3 bg-muted/30 rounded-tl-xl border-b border-border text-foreground font-semibold min-w-[200px]">
                Attività
              </th>
              {weekData.map((d, idx) => (
                <th 
                  key={d.date} 
                  className={`p-3 bg-muted/30 border-b border-border text-center min-w-[120px] ${
                    idx === 6 ? "rounded-tr-xl" : ""
                  } ${isToday(d.day) ? "bg-primary/5" : ""}`}
                >
                  <div className={`font-semibold ${isToday(d.day) ? "text-primary" : "text-foreground"}`}>
                    {GIORNI_SETTIMANA[idx]}
                  </div>
                  <div className="text-xs text-muted-foreground font-medium">{d.day.getDate()}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allTasks.map((taskDef, rowIdx) => {
              let icon = null;
              let typeColor = "";
              
              if (taskDef.tipo_attivita === "pulizia") {
                icon = <Sparkles className="h-3 w-3" />;
                typeColor = "text-cyan-600";
              } else if (taskDef.tipo_attivita === "gestione") {
                icon = <Briefcase className="h-3 w-3" />;
                typeColor = "text-orange-600";
              } else {
                icon = <Thermometer className="h-3 w-3" />;
                typeColor = "text-blue-600";
              }
              
              return (
                <tr key={`${taskDef.tipo_attivita}-${taskDef.nome}`} className="border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors">
                  <td className={`p-3 ${rowIdx === allTasks.length - 1 ? "rounded-bl-xl" : ""}`}>
                    <div className="flex items-center gap-2">
                      <span className={typeColor}>{icon}</span>
                      <span className="text-foreground text-sm font-medium">{taskDef.nome}</span>
                    </div>
                    {taskDef.area_nome && (
                      <span className="text-xs text-muted-foreground ml-5">{taskDef.area_nome}</span>
                    )}
                    {/* Operatori assegnati (solo per task gestione) */}
                    {taskDef.tipo_attivita === "gestione" && taskDef.operatori_ids?.length > 0 && (
                      <div className="flex items-center gap-1 mt-1 ml-5">
                        <User className="h-3 w-3 text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground">
                          {taskDef.operatori_ids.map(id => getOperatoreNome(id)).filter(Boolean).join(", ") || "Assegnati"}
                        </span>
                      </div>
                    )}
                  </td>
                  {weekData.map((dayData, colIdx) => {
                    const tasks = getFilteredTasks(dayData);
                    const task = tasks.find(t => 
                      t.tipo_attivita === taskDef.tipo_attivita && t.nome === taskDef.nome
                    );
                    
                    const isLast = rowIdx === allTasks.length - 1 && colIdx === 6;
                    
                    return (
                      <td 
                        key={`${dayData.date}-${taskDef.nome}`} 
                        className={`p-2 text-center ${isToday(dayData.day) ? "bg-primary/5" : ""} ${isLast ? "rounded-br-xl" : ""}`}
                      >
                        {task ? (
                          <div className="flex flex-col items-center gap-1">
                            {task.completata ? (
                              <>
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                  task.is_anomala ? "bg-red-100" : "bg-green-100"
                                }`}>
                                  {task.is_anomala ? (
                                    <AlertTriangle className="h-3 w-3 text-red-600" />
                                  ) : (
                                    <Check className="h-3 w-3 text-green-600" />
                                  )}
                                </div>
                                {task.temperatura !== undefined && (
                                  <span className={`text-xs font-bold ${task.is_anomala ? "text-red-600" : "text-blue-600"}`}>
                                    {task.temperatura}°C
                                  </span>
                                )}
                                {/* Operatore che ha completato */}
                                {(task.operatore_nome || task.operatore) && (
                                  <span className="text-[10px] text-green-700 font-medium truncate max-w-[80px]" title={task.operatore_nome || task.operatore}>
                                    {task.operatore_nome || task.operatore}
                                  </span>
                                )}
                                {(task.ora_completamento || task.ora_registrazione) && (
                                  <span className="text-[10px] text-slate-500">
                                    {task.ora_completamento || task.ora_registrazione}
                                  </span>
                                )}
                              </>
                            ) : (
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                task.tipo === "informativa" ? "bg-purple-100" : "bg-amber-100"
                              }`}>
                                {task.tipo === "informativa" ? (
                                  <AlertCircle className="h-3 w-3 text-purple-600" />
                                ) : (
                                  <X className="h-3 w-3 text-amber-600" />
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading && !dayData.pulizie?.length && !weekData.length) {
    return (
      <div className="loading-state">
        <div className="text-muted-custom">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="calendario-admin-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-lime-400" />
              Calendario
            </h1>
            <p className="page-subtitle">Monitora tutte le attività completate</p>
          </div>
        </div>

        {/* Date Navigator */}
        <div className="flex items-center justify-between bg-card rounded-2xl p-3 border border-border shadow-sm">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigateDate(-1)}
            className="text-muted-foreground hover:text-foreground hover:bg-muted/50"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          
          <div className="text-center">
            <p className="text-foreground font-bold capitalize">{getDateDisplay()}</p>
            {!isToday(currentDate) && (
              <button onClick={goToToday} className="text-primary text-xs mt-1 hover:underline font-medium">
                Vai ad oggi
              </button>
            )}
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigateDate(1)}
            className="text-muted-foreground hover:text-foreground hover:bg-muted/50"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        {/* Controls Row */}
        <div className="flex flex-wrap gap-3">
          {/* View Mode: Giorno / Settimana */}
          <div className="flex-1 min-w-[200px] flex bg-muted border border-border p-1 h-12 rounded-xl">
            <button
              onClick={() => setViewMode("giorno")}
              className={`flex-1 h-10 rounded-lg font-semibold transition-colors ${
                viewMode === "giorno" 
                  ? "bg-primary text-white shadow-sm" 
                  : "text-foreground hover:bg-muted/80"
              }`}
            >
              Giorno
            </button>
            <button
              onClick={() => setViewMode("settimana")}
              className={`flex-1 h-10 rounded-lg font-semibold transition-colors ${
                viewMode === "settimana" 
                  ? "bg-primary text-white shadow-sm" 
                  : "text-foreground hover:bg-muted/80"
              }`}
            >
              Settimana
            </button>
          </div>

          {/* Display Mode rimosso — solo tabella in vista settimana */}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <span className="flex items-center gap-1 text-muted-foreground text-sm mr-2 font-medium">
            <Filter className="h-4 w-4" />
            Filtra:
          </span>
          {FILTRI_TIPO.map(filtro => {
            const Icon = filtro.icon;
            return (
              <button
                key={filtro.value}
                onClick={() => setFiltroTipo(filtro.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  filtroTipo === filtro.value
                    ? "bg-primary text-white"
                    : "bg-muted text-foreground hover:bg-muted/80 border border-border"
                }`}
              >
                <Icon className={`h-3.5 w-3.5 ${filtroTipo === filtro.value ? "" : filtro.color || ""}`} />
                {filtro.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      {viewMode === "giorno" ? (
        <div className="space-y-4">
          {/* Stats */}
          {renderStats(dayData.stats)}
          
          {/* Task List */}
          <div className="space-y-2">
            {getFilteredTasks(dayData).length === 0 ? (
              <div className="text-center py-12 text-muted-foreground bg-card rounded-2xl border border-border">
                <CalendarDays className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
                <p>Nessuna attività per questo giorno</p>
              </div>
            ) : (
              getFilteredTasks(dayData).map(task => renderTaskItem(task))
            )}
          </div>
        </div>
      ) : (
        // Settimana view — solo tabella
        <div className="space-y-4">
          {renderWeekTable()}
        </div>
      )}
    </div>
  );
};

export default CalendarioAdmin;
